/**
 * Which theme the interface is drawn in, and how that choice survives a
 * reload.
 *
 * Three states, and LIGHT is the default — not "system". Everything else the
 * product owns is light: 2 386 static landing pages, the OG image, the
 * brand. Following the OS by default would mean a visitor with a dark
 * desktop reads a white landing page, clicks the CTA and lands in a dark
 * app — a seam, on the one path that matters most. Dark is a choice someone
 * makes, and "system" is a choice too: pick it and the theme follows the OS,
 * including when it changes at lunchtime, without a reload.
 *
 * The RESOLVED theme is always written to `<html data-theme>`, never left to
 * a `prefers-color-scheme` media query, and that is the load-bearing
 * decision. The static landing pages carry the same compiled stylesheet and
 * no script of ours, so a rule guarded by the media query would have turned
 * 2 386 pages dark for anyone whose OS is dark — pages nobody has designed a
 * dark version of. An attribute only the app ever sets cannot leak there.
 * The cost is that the theme needs JavaScript, which the app needs anyway.
 */
export type ThemeChoice = 'light' | 'dark' | 'system'
export type ResolvedTheme = 'light' | 'dark'

export const THEME_STORAGE_KEY = 'stt_theme'

/** What the browser tab's chrome is painted with. Kept in step with the
 * theme: a blue bar over a dark app is the one piece of the page we do not
 * own and the one people notice. */
const THEME_COLOR: Record<ResolvedTheme, string> = {
  // Light keeps the brand blue the product has always shipped — the static
  // pages' heads say the same thing, and a dark theme is no reason to
  // restyle the light one. Dark takes the app's own ground instead: blue
  // chrome above a near-black page is the seam people notice.
  light: '#3A5FF7',
  dark: '#1a1d24',
}

export function isThemeChoice(v: unknown): v is ThemeChoice {
  return v === 'light' || v === 'dark' || v === 'system'
}

/** The stored choice, or 'light' when there is none — and also when storage
 * cannot be read, because a private window should get the product's own
 * default rather than a third behaviour nobody chose. */
export function readThemeChoice(storage?: Pick<Storage, 'getItem'>): ThemeChoice {
  try {
    const s = storage ?? localStorage
    const saved = s.getItem(THEME_STORAGE_KEY)
    return isThemeChoice(saved) ? saved : 'light'
  } catch {
    return 'light'
  }
}

/** What to actually draw. `systemPrefersDark` is passed in rather than read
 * here so the resolution is testable without a media-query stub. */
export function resolveTheme(choice: ThemeChoice, systemPrefersDark: boolean): ResolvedTheme {
  if (choice === 'system') return systemPrefersDark ? 'dark' : 'light'
  return choice
}

export function systemPrefersDark(): boolean {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches
  } catch {
    // No matchMedia (old browser, some test environments) — light is the
    // safer guess: it is what every surface of this product was designed in
    return false
  }
}

/** Put the resolved theme on the document. Everything visual keys off this
 * one attribute — the token overrides, `color-scheme` for native controls,
 * and the `dark:` variant for the handful of things a token cannot say. */
export function applyTheme(resolved: ResolvedTheme, doc: Document = document): void {
  doc.documentElement.setAttribute('data-theme', resolved)
  const meta = doc.querySelector('meta[name="theme-color"]')
  if (meta) meta.setAttribute('content', THEME_COLOR[resolved])
}

export function storeThemeChoice(choice: ThemeChoice): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, choice)
  } catch {
    // Storage unavailable — the choice lives for this session only
  }
}

/**
 * Follow the OS while the choice is 'system'.
 *
 * Returns an unsubscribe function. Listening only matters in that state, but
 * subscribing unconditionally and checking inside would fire on every OS
 * change for someone who has explicitly chosen — so the caller re-subscribes
 * when the choice changes instead.
 */
export function watchSystemTheme(onChange: (prefersDark: boolean) => void): () => void {
  let mq: MediaQueryList
  try {
    mq = window.matchMedia('(prefers-color-scheme: dark)')
  } catch {
    return () => {}
  }
  const handler = (e: MediaQueryListEvent) => onChange(e.matches)
  mq.addEventListener('change', handler)
  return () => mq.removeEventListener('change', handler)
}
